import React, { useMemo } from "react";
import useAxios from "axios-hooks";

export const useActionItemLabelLookups = () => {
  const [{ data: topicsData }] = useAxios("resources/topics/titles", {
    useCache: true,
  });
  const [{ data: journalData }] = useAxios("journal/categoriesPromptsAudit", {
    useCache: true,
  });

  return useMemo(() => {
    const topicNameById = {};
    if (Array.isArray(topicsData)) {
      topicsData.forEach((t) => {
        topicNameById[t.topicId] = t.topicName;
      });
    }

    const categoryNameById = {};
    (journalData?.categories || []).forEach((c) => {
      categoryNameById[c.journalCategoryId] = c.category;
    });
    const journalPromptById = {};
    (journalData?.prompts || []).forEach((p) => {
      journalPromptById[p.journalPromptId] = {
        prompt: p.prompt,
        categoryName: categoryNameById[p.journalCategoryId],
      };
    });

    return { topicNameById, journalPromptById };
  }, [topicsData, journalData]);
};

export const stripHtml = (html) => {
  if (!html) return "";
  return String(html).replace(/<[^>]*>/g, "").trim();
};

// Server HTML-encodes journal briefs; DOMParser decodes without executing anything.
const decodeEntities = (text) => {
  if (!text) return "";
  return new DOMParser().parseFromString(text, "text/html").documentElement.textContent;
};

export const extractItalicTarget = (html) => {
  if (!html) return null;
  const match = String(html).match(/<i[^>]*>([\s\S]*?)<\/i>/i);
  return match ? stripHtml(match[1]) : null;
};

export const getActionLabel = (item, lookups) => {
  if (!item) return { action: "", target: null, chips: [] };
  const { topicNameById, journalPromptById } = lookups || {};
  switch (item.actionItemType) {
    case "Proprietary Specific": {
      const chips = [];
      if (item.minimumScore != null && item.minimumScore !== "") {
        chips.push(`Min score ${item.minimumScore}%`);
      }
      // Journal titles are plain "Journal about: <brief>" with no italic target, so every
      // journal item would otherwise render identically.
      if (item.proprietarySpecificJournalPromptID) {
        const journalPrompt = journalPromptById?.[item.proprietarySpecificJournalPromptID];
        if (journalPrompt?.categoryName) {
          chips.push(journalPrompt.categoryName);
        }
        return {
          action: item.proprietarySpecificActionType || "Proprietary Specific",
          target:
            decodeEntities(stripHtml(item.actionItemTitle)) || stripHtml(journalPrompt?.prompt),
          chips,
          tooltip: journalPrompt?.prompt,
        };
      }
      return {
        action: item.proprietarySpecificActionType || "Proprietary Specific",
        target: extractItalicTarget(item.actionItemTitle),
        chips,
      };
    }
    case "Proprietary General": {
      const chips = [];
      if (item.proprietaryGeneralAmount != null && item.proprietaryGeneralAmount !== "") {
        chips.push(`Amount: ${item.proprietaryGeneralAmount}`);
      }
      if (item.minimumScore != null && item.minimumScore !== "") {
        chips.push(`Min score ${item.minimumScore}%`);
      }
      const topicName =
        item.proprietaryGeneralTopicID && topicNameById
          ? topicNameById[item.proprietaryGeneralTopicID]
          : null;
      const target = item.proprietaryGeneralTopicID
        ? `Topic: ${topicName || `#${item.proprietaryGeneralTopicID}`}`
        : extractItalicTarget(item.actionItemTitle);
      return {
        action: item.proprietaryGeneralActionType || "Proprietary General",
        target,
        chips,
      };
    }
    case "Third Party": {
      const chips = [];
      if (item.thirdPartyCompletionMechanism) {
        chips.push(item.thirdPartyCompletionMechanism);
      }
      return {
        action: "Third Party",
        target: stripHtml(item.thirdPartyActionTitle) || extractItalicTarget(item.actionItemTitle),
        chips,
      };
    }
    default:
      return {
        action: item.actionItemType || "Action Item",
        target: stripHtml(item.actionItemTitle),
        chips: [],
      };
  }
};

export const Chip = ({ children }) => (
  <span
    style={{
      display: "inline-block",
      padding: "1px 6px",
      marginLeft: 6,
      borderRadius: 8,
      backgroundColor: "#EFF6FC",
      color: "#005A9E",
      fontSize: 11,
      lineHeight: "16px",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </span>
);

export const ActionItemLabel = ({ item, lookups }) => {
  const { action, target, chips, tooltip } = getActionLabel(item, lookups);
  return (
    <div style={{ display: "flex", flexDirection: "column", lineHeight: "18px", overflow: "hidden" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          fontWeight: 600,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{action}</span>
        {chips.map((c) => (
          <Chip key={c}>{c}</Chip>
        ))}
      </div>
      {target && (
        <div
          style={{
            color: "#605E5C",
            fontSize: 12,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={tooltip || target}
        >
          {target}
        </div>
      )}
    </div>
  );
};
